import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
} from '@mui/material';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { memo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormTextField from 'components/ui/form/FormTextField';
import FormTreeSelectField from 'components/ui/form/FormTreeSelectField';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useFormSubmit from 'hooks/useFormSubmit';
import useNotification from 'hooks/useNotification';

const validationSchema = yup.object({
  name: yup.string().required('settings.crm.webform.validation.name'),
  title: yup.string().required('settings.crm.webform.validation.title'),
});

const EMPTY = {
  name: '',
  title: '',
  description: '',
  button_text: '',
  success_text: '',
  contact_phone: '',
  website: '',
  crm_block_id: '',
  crm_block_item_id: '',
  status: 1,
};

// smartsales'da loyihani tanitadigan backend URL — .env REACT_APP_BACKEND_URL (projects.url bilan bir xil)
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const TA_CLASS =
  'w-full base-border rounded-lg px-3 py-2 text-sm text-gray-700 bg-white outline-none focus:border-[#0057FF] resize-y';

/**
 * Web formalar — smartsales.uz'da joylashadigan zayavka formalari.
 * Forma saqlangach backbusiness uni smartsales'ga sync qiladi va unikal havola oladi;
 * havola orqali kelgan lidlar tanlangan CRM etapiga (WebFormLead bilan) tushadi.
 */
const WebFormsModal = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const sendNotification = useNotification();
  const { submit, isSubmitting } = useFormSubmit();
  const [mode, setMode] = useState('list'); // list | edit
  const [editing, setEditing] = useState(null);
  const [leadsFor, setLeadsFor] = useState(null);
  const [syncing, setSyncing] = useState(null);

  const formsQuery = useQuery({
    queryKey: ['/admin/webforms'],
    queryFn: async () => {
      const response = await axiosPrivate.get('/admin/webforms');
      return response.data.data || [];
    },
    enabled: open,
    retry: false,
  });
  const forms = formsQuery.data || [];

  const leadsQuery = useQuery({
    queryKey: ['/admin/webforms/leads', leadsFor],
    queryFn: async () => {
      const response = await axiosPrivate.get(`/admin/webforms/${leadsFor}/leads`);
      return response.data.data?.data || [];
    },
    enabled: open && !!leadsFor,
    retry: false,
  });

  const formik = useFormik({
    initialValues: EMPTY,
    validationSchema,
    onSubmit: async (values) => {
      submit(
        { type: 'post', contentType: 'simple' },
        { ...values, backend_url: BACKEND_URL },
        editing ? `/admin/webforms/${editing.id}` : '/admin/webforms',
        { title: t('settings.crm.webform.saved') },
        null,
        true,
        () => {
          formsQuery.refetch();
          setMode('list');
          setEditing(null);
        },
      );
    },
  });

  const openCreate = () => {
    setEditing(null);
    formik.resetForm({ values: EMPTY });
    setMode('edit');
  };

  const openEdit = (form) => {
    setEditing(form);
    formik.resetForm({
      values: {
        ...EMPTY,
        name: form.name || '',
        title: form.title || '',
        description: form.description || '',
        button_text: form.button_text || '',
        success_text: form.success_text || '',
        contact_phone: form.contact_phone || '',
        website: form.website || '',
        crm_block_id: form.crm_block_item?.crm_block_id || '',
        crm_block_item_id: form.crm_block_item_id || '',
        status: Number(form.status ?? 1),
      },
    });
    setMode('edit');
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      sendNotification({ msg: t('settings.crm.webform.copied'), variant: 'success' });
    } catch (e) {
      window.prompt(t('settings.crm.webform.link'), link);
    }
  };

  // Havola olinmagan bo'lsa — smartsales bilan sync'ni qayta urinish
  const syncForm = async (form) => {
    setSyncing(form.id);
    try {
      await axiosPrivate.post(`/admin/webforms/${form.id}/sync`, { backend_url: BACKEND_URL });
      sendNotification({ msg: t('settings.crm.webform.synced'), variant: 'success' });
      formsQuery.refetch();
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || error?.message,
        variant: 'error',
      });
    } finally {
      setSyncing(null);
    }
  };

  const toggleStatus = async (form, checked) => {
    try {
      await axiosPrivate.post(`/admin/webforms/${form.id}`, {
        name: form.name,
        title: form.title,
        description: form.description,
        button_text: form.button_text,
        success_text: form.success_text,
        contact_phone: form.contact_phone,
        website: form.website,
        crm_block_item_id: form.crm_block_item_id,
        status: checked ? 1 : 0,
        backend_url: BACKEND_URL,
      });
      formsQuery.refetch();
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || error?.message,
        variant: 'error',
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setMode('list');
    setEditing(null);
    setLeadsFor(null);
  };

  const textarea = (field, label, rows = 3) => (
    <div>
      <label htmlFor={`webform-${field}`} className='block text-xs text-gray-500 mb-1'>
        {label}
      </label>
      <textarea
        id={`webform-${field}`}
        rows={rows}
        className={`${TA_CLASS} ${
          formik.touched[field] && formik.errors[field] ? '!border-red-500' : ''
        }`}
        value={formik.values[field]}
        onChange={(e) => formik.setFieldValue(field, e.target.value)}
        onBlur={() => formik.setFieldTouched(field, true)}
      />
      {formik.touched[field] && formik.errors[field] ? (
        <div className='text-xs text-red-500 mt-1'>{t(formik.errors[field])}</div>
      ) : null}
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      maxWidth='md'
      PaperProps={{ className: '!base-border !shadow-none min-w-[60vw]' }}
      disableEscapeKeyDown={true}
    >
      <DialogTitle id='alert-dialog-title' className='!bg-gray-100'>
        <div className='flex items-center justify-between'>
          <div>
            <span>{t('settings.crm.webform.title')}</span>
            <div className='text-xs text-gray-500 font-normal mt-1'>
              {t('settings.crm.webform.subtitle')}
            </div>
          </div>
          <IconButton size='small' onClick={handleClose}>
            <i className='bi bi-x-lg' />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent className='!pt-4'>
        {mode === 'edit' ? (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={{ xs: 2, sm: 2, md: 2 }} rowSpacing={1} columns={{ xs: 12 }}>
              <Grid item={true} md={6} sm={12} xs={12}>
                <FormTextField
                  delay={0}
                  label={t('settings.crm.webform.name')}
                  fieldName='name'
                  formik={formik}
                />
              </Grid>
              <Grid item={true} md={6} sm={12} xs={12}>
                <FormTextField
                  delay={0}
                  label={t('settings.crm.webform.buttonText')}
                  fieldName='button_text'
                  formik={formik}
                />
              </Grid>
              <Grid item={true} sm={12} xs={12}>
                {textarea('title', t('settings.crm.webform.formTitle'), 3)}
              </Grid>
              <Grid item={true} sm={12} xs={12}>
                {textarea('description', t('settings.crm.webform.description'), 2)}
              </Grid>
              <Grid item={true} sm={12} xs={12}>
                {textarea('success_text', t('settings.crm.webform.successText'), 2)}
              </Grid>
              <Grid item={true} md={6} sm={12} xs={12}>
                <FormTextField
                  delay={0}
                  label={t('settings.crm.webform.contactPhone')}
                  fieldName='contact_phone'
                  formik={formik}
                />
              </Grid>
              <Grid item={true} md={6} sm={12} xs={12}>
                <FormTextField
                  delay={0}
                  label={t('settings.crm.webform.website')}
                  fieldName='website'
                  formik={formik}
                />
              </Grid>
              <Grid item={true} sm={12} xs={12}>
                <span className='text-sm'>{t('settings.crm.webform.stage')}</span>
                <div className='border border-gray-400 mt-2 p-[5px] rounded-lg [&>*]:!m-0'>
                  <FormTreeSelectField
                    parentFieldName='crm_block_id'
                    childFieldName='crm_block_item_id'
                    label=''
                    childOptionField={'pipes'}
                    formik={formik}
                    parentLabel={'name'}
                    parentValue={'id'}
                    childLabel={'name'}
                    childValue={'id'}
                    variant='standard'
                    path='/crm/block'
                    size='small'
                    onChange={() => {}}
                    progress={false}
                  />
                </div>
              </Grid>
              <Grid item={true} sm={12} xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Number(formik.values.status) === 1}
                      onChange={(e) => formik.setFieldValue('status', e.target.checked ? 1 : 0)}
                    />
                  }
                  label={t('settings.crm.webform.active')}
                />
              </Grid>
              <Grid item={true} sm={12} xs={12}>
                <FormActionButtons
                  delay={0}
                  isSubmitting={isSubmitting}
                  formType='dialog'
                  setOpen={() => setMode('list')}
                  reset={formik.resetForm}
                />
              </Grid>
            </Grid>
          </form>
        ) : (
          <div>
            <div className='flex justify-end mb-3'>
              <Button
                variant='contained'
                color='primary'
                startIcon={<i className='bi bi-plus-circle' />}
                onClick={openCreate}
              >
                {t('settings.crm.webform.add')}
              </Button>
            </div>

            {formsQuery.isLoading ? (
              <div className='flex justify-center p-6'>
                <CircularProgress size={28} />
              </div>
            ) : forms.length === 0 ? (
              <div className='text-gray-400 text-sm text-center py-6'>
                {t('settings.crm.webform.noForms')}
              </div>
            ) : (
              <div className='flex flex-col gap-3'>
                {forms.map((form) => (
                  <div key={form.id} className='base-border rounded-lg p-3 bg-white'>
                    <div className='flex items-start justify-between gap-3'>
                      <div className='min-w-0'>
                        <div className='font-semibold text-gray-800 truncate'>{form.name}</div>
                        <div className='text-xs text-gray-500 mt-0.5'>
                          {form.crm_block_item?.name || '—'} ·{' '}
                          {t('settings.crm.webform.leads')}: {form.leads_count ?? 0}
                        </div>
                        {form.link ? (
                          <div className='flex items-center gap-2 mt-2'>
                            <a
                              href={form.link}
                              target='_blank'
                              rel='noreferrer'
                              className='text-sm text-blue-600 truncate max-w-[420px]'
                            >
                              {form.link}
                            </a>
                            <IconButton
                              size='small'
                              title={t('settings.crm.webform.copy')}
                              onClick={() => copyLink(form.link)}
                            >
                              <i className='bi bi-clipboard' />
                            </IconButton>
                          </div>
                        ) : (
                          <div className='flex items-center gap-2 mt-2'>
                            <span className='text-xs text-amber-600'>
                              {t('settings.crm.webform.noLink')}
                              {form.sync_error ? `: ${form.sync_error}` : ''}
                            </span>
                            <Button
                              size='small'
                              variant='base'
                              color='primary'
                              className='!text-[12px]'
                              disabled={syncing === form.id}
                              onClick={() => syncForm(form)}
                            >
                              {t('settings.crm.webform.sync')}
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className='flex items-center gap-1 shrink-0'>
                        <Switch
                          size='small'
                          checked={Number(form.status) === 1}
                          onChange={(e) => toggleStatus(form, e.target.checked)}
                          title={t('settings.crm.webform.active')}
                        />
                        <Button
                          size='small'
                          variant='base'
                          color='default'
                          className='!text-[12px]'
                          onClick={() => setLeadsFor(leadsFor === form.id ? null : form.id)}
                        >
                          <i className='bi bi-people mr-1' />
                          {t('settings.crm.webform.leads')}
                        </Button>
                        <IconButton size='small' onClick={() => openEdit(form)}>
                          <i className='bi bi-pen' />
                        </IconButton>
                      </div>
                    </div>

                    {leadsFor === form.id && (
                      <div className='mt-3 border-t pt-2'>
                        {leadsQuery.isLoading ? (
                          <div className='flex justify-center p-3'>
                            <CircularProgress size={20} />
                          </div>
                        ) : (leadsQuery.data || []).length === 0 ? (
                          <div className='text-xs text-gray-400 py-2'>
                            {t('settings.crm.webform.noLeads')}
                          </div>
                        ) : (
                          <div className='max-h-[240px] overflow-y-auto my-scroll'>
                            {leadsQuery.data.map((lead) => (
                              <div
                                key={lead.id}
                                className='flex items-center justify-between text-sm py-1.5 px-1 odd:bg-gray-50'
                              >
                                <div className='flex items-center gap-3 min-w-0'>
                                  <span className='text-gray-400 text-xs whitespace-nowrap'>
                                    {lead.created_at
                                      ? moment(lead.created_at).format('DD.MM.YYYY HH:mm')
                                      : ''}
                                  </span>
                                  {lead.crm_lead_id ? (
                                    <Link
                                      className='no-underline text-blue-600 truncate'
                                      to={`/admin/crm/lead/edit/${lead.crm_lead_id}`}
                                    >
                                      {lead.name || lead.phone}
                                    </Link>
                                  ) : (
                                    <span className='truncate'>{lead.name || lead.phone}</span>
                                  )}
                                  <span className='text-gray-500 whitespace-nowrap'>{lead.phone}</span>
                                </div>
                                <span className='text-xs text-gray-500 whitespace-nowrap'>
                                  {lead.crm_lead?.user?.name || ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default memo(WebFormsModal);
